/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import { Text, View } from '@az/starc-ui';
import React from 'react';
import { formatNumber } from '../../../utils/formatNumber';
import type { ReviewStatistic } from '../../../interface/types';
import { useReviewsOverviewProps } from '../hooks/useReviewsOverviewProps';
import styles from '../styles.module.scss';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { ReviewRatingsAndReviews } from '../ReviewRatingsAndReviews/ReviewRatingsAndReviews';
import { RatingDistributions } from '../RatingDistributions/RatingDistributions';
import { BVTrustMark } from '../BVTrustMark/BVTrustMark';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type ReviewsOverviewContentProps = {
  reviewStatistic: ReviewStatistic | undefined;
  reviewCount: string;
  recommendedCount: number;
};

export const ReviewsOverviewContent = ({
  reviewStatistic,
  reviewCount,
  recommendedCount,
}: ReviewsOverviewContentProps) => {
  const pdpRedesignEnabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const { labels } = useReviewsOverviewProps({
    reviewStatistic,
    reviewCount,
    recommendedCount,
  });
  const showRecommendPercentage =
    recommendedCount > 0 && reviewStatistic?.recommendPercentage != null;
  const showTrustMark = Boolean(reviewStatistic?.intelligentTrustMarkDisplayEligible);
  const showRecommendRow = showRecommendPercentage || showTrustMark;

  return (
    <FlexGrid
      flexDirection="column"
      gap={{ sm: 6, md: 8, lg: pdpRedesignEnabled ? 8 : 6 }}
      className={styles.overviewContainer}
    >
      <FlexGrid flexDirection="column" gap={2}>
        <View className={styles.overviewV2FirstRow}>
          {reviewStatistic ? (
            <ReviewRatingsAndReviews
              reviewStatistic={reviewStatistic}
              reviewCount={`${reviewCount} ${
                Number(reviewCount) === 1 ? labels.singleReview : labels.reviews
              }`}
              recommendedCount={recommendedCount}
            />
          ) : null}
        </View>
        {showRecommendRow && (
          <View direction="row" align="center" gap={{ s: 2, m: 4, l: 2 }}>
            {showRecommendPercentage && (
              <Text variant="main-body" weight="bold" className={styles.reviewersRecommend}>
                {`${formatNumber(Number(reviewStatistic.recommendPercentage), 0)}% ${pdpRedesignEnabled ? labels.recommended : labels.recommendThis}`}
              </Text>
            )}
            {showTrustMark && <BVTrustMark />}
          </View>
        )}
      </FlexGrid>

      <View className={cx(styles.distributions, { [styles['--fullWidth']]: !pdpRedesignEnabled })}>
        <RatingDistributions />
      </View>
    </FlexGrid>
  );
};
